import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { DueDayPicker } from "@components/DueDayPicker/DueDayPicker";
import { FeatureFlag } from "@components/FeatureFlag/FeatureFlag";
import { PencilIcon } from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Input } from "@components/inputs/Input/Input";
import { Label } from "@components/Label/Label";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import {
  type IBillData,
  type IOverviewJointBillService,
} from "@services/overview/types";
import {
  useEditServiceProviderDueDayMutation,
  useEditServiceProviderMutation,
} from "@services/serviceProviders/mutations";
import { isNullOrUndefined } from "@utils/common";
import { formatDate } from "@utils/units";
import { useEditServiceProviderSettingsModalSchema } from "./useEditServiceProviderSettingsModalSchema";

interface IEditServiceProviderSettingsModalProps extends IUseModalProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  billData: IBillData;
  billsCount: number;
}

interface IFormData {
  displayName?: string;
  customerCode?: string;
  dueDay?: number;
  ibanNumber?: string;
}

export const EditServiceProviderSettingsModal: React.FC<
  IEditServiceProviderSettingsModalProps
> = (props) => {
  const { jointBillServiceId, billData, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();

  const schema = useEditServiceProviderSettingsModalSchema(billData);

  const {
    handleSubmit,
    control,
    setError,
    watch,
    formState,
    setValue,
    getFieldState,
  } = useForm<IFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      displayName: billData.displayName,
      customerCode: billData.customerCode,
      dueDay: billData.dueDate
        ? Number(formatDate(billData.dueDate, "d"))
        : undefined,
      ibanNumber: billData.ibanNumber,
    },
  });

  const dueDay = watch("dueDay");

  const { setApiErrors } = useApiErrorUtils(setError);

  const { mutateAsync: editServiceProvider } = useEditServiceProviderMutation(
    jointBillServiceId,
    billData.jointBillServiceAgreementId,
  );

  const { mutateAsync: editServiceProviderDueDate } =
    useEditServiceProviderDueDayMutation();

  const submitForm: SubmitHandler<IFormData> = async (data) => {
    if (getFieldState("dueDay").isDirty) {
      await editServiceProviderDueDate({
        day: dueDay,
        jointBillServiceAgreementId: billData.jointBillServiceAgreementId,
        jointBillServiceId: jointBillServiceId,
      }).catch(setApiErrors);
    }

    const response = await editServiceProvider({
      ...(billData.canEditServiceProviderDisplayName
        ? {
            displayName: data.displayName,
          }
        : {}),
      ...(billData.canEditServiceProviderCustomerCode
        ? {
            customerCode: data.customerCode,
          }
        : {}),
      ...(billData.canIbanNumberBeEdited
        ? {
            ibanNumber: data.ibanNumber,
          }
        : {}),
    }).catch(setApiErrors);

    if (!isNullOrUndefined(response)) {
      modal.close();
      toast.success(t("editServiceProviderSettingsModal.toast.success"));
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: PencilIcon }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("editServiceProviderSettingsModal.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(submitForm)}
            isLoading={formState.isSubmitting}
          >
            {t("editServiceProviderSettingsModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
      title={t("editServiceProviderSettingsModal.title")}
      subtitle={billData.serviceProviderName}
    >
      <form className="flex flex-col gap-4">
        <FormInput
          name="displayName"
          control={control}
          label={t(
            "editServiceProviderSettingsModal.form.fields.displayName.label",
          )}
          disabled={!billData.canEditServiceProviderDisplayName}
        />
        {billData.customerCode && (
          <FormInput
            name="customerCode"
            control={control}
            label={t(
              "editServiceProviderSettingsModal.form.fields.customerCode.label",
            )}
            disabled={!billData.canEditServiceProviderCustomerCode}
          />
        )}
        {billData.paymentInstruction && (
          <React.Fragment>
            <Input
              label={t(
                "editServiceProviderSettingsModal.form.fields.personalCode.label",
              )}
              value={billData.paymentInstruction.personalCode}
              disabled
            />
            <Input
              label={t(
                "editServiceProviderSettingsModal.form.fields.fullName.label",
              )}
              value={billData.paymentInstruction.fullName}
              disabled
            />
            <div className="flex flex-col gap-1.5">
              <Input
                label={t(
                  "editServiceProviderSettingsModal.form.fields.paymentCode.label",
                )}
                value={billData.paymentInstruction.paymentCode}
                disabled
              />
              <Label className="cursor-auto text-xs">
                {billData.paymentInstruction.paymentCodeDescription}
              </Label>
            </div>
            <Input
              label={t(
                "editServiceProviderSettingsModal.form.fields.paymentPurpose.label",
              )}
              value={billData.paymentInstruction.paymentPurpose}
              disabled
            />
            {!isNullOrUndefined(billData.paymentInstruction.roikCode) && (
              <Input
                label={t(
                  "editServiceProviderSettingsModal.form.fields.roikCode.label",
                )}
                value={billData.paymentInstruction.roikCode}
                disabled
              />
            )}
          </React.Fragment>
        )}
        {billData.ibanNumber && (
          <FormInput
            name="ibanNumber"
            control={control}
            label={t(
              "editServiceProviderSettingsModal.form.fields.ibanNumber.label",
            )}
            disabled={!billData.canIbanNumberBeEdited}
          />
        )}
        <FeatureFlag flag={["isDueDateEnabled"]}>
          <DueDayPicker
            value={dueDay}
            onChange={(val) => {
              setValue("dueDay", val, { shouldDirty: true });
            }}
          />
        </FeatureFlag>
      </form>
    </Modal>
  );
};
